import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { PermissionsEnum } from "@/auth/interfaces";
import {
  // gridSizeMdLg12,
  useCheckPermissionsArray,
  useLoaders,
  // useLoaders,
  useMapComponent,
} from "@/shared";
import { getIpv4Groups, useFetchAllNodes, useFetchOLTs, 
  // useFetchAllNodes, useFetchOLTs 
} from "@/shared/actions/erp";
import {
  CustomAutocomplete,
  // CustomAutocomplete,
  CustomAutocompleteArrString,
  CustomDynamicSearch,
  CustomNumberTextField,
  CustomPasswordTextField,
  CustomTextField,
  SingleFormBoxScene,
  SwitchComponenteForm,
} from "@/shared/components/ui";
import { TIPOS_ROUTER_CHOICE, gridSizeMdLg6 } from "@/shared/constants";
import { AlertSeverityEnum, Ipv4Group, Nodo, OLT, Router } from "@/shared/interfaces";
// import { Nodo } from "@/shared/interfaces/erp/mantenimiento-operaciones";
import { routerFormSchema, texto } from "@/shared/utils";
import {
  CreateRouterParams,
  useCreateRouter,
  useUpdateRouter,
} from "@/store/erp/router";
import { useUiStore } from "@/store/ui";
import GlobeEuropeAfricaIcon from "@heroicons/react/24/solid/GlobeEuropeAfricaIcon";
import { InputAdornment, SvgIcon } from "@mui/material";
import CustomAutocompleteOptimized from "@/shared/components/ui/CustomAutocompleteOptimized";

export interface SaveRouterProps {
  title: string;
  router?: Router;
}

type SaveFormData = CreateRouterParams & {};

const SaveRouter: React.FC<SaveRouterProps> = ({ title, router }) => {
  useCheckPermissionsArray([
    PermissionsEnum.api_view_nodo,
    PermissionsEnum.api_view_olt,
  ]);
  const returnUrlRoutesrPage = "/configuracion-red/routers";

  const navigate = useNavigate();

  ///* global state
  const setAlert = useUiStore((s) => s.setAlert);

  ///* form
  const form = useForm<SaveFormData>({
    resolver: yupResolver(routerFormSchema as any),
    defaultValues: {
      estado: true,
      puerto_api: null as any,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
    setValue,
  } = form;

  const watchedNodo = form.watch("nodo");

  // map
  const { Map, latLng, setLatLng, setCurrentCoordenadas, currentCoordenadas } =
    useMapComponent({
      form,
      currentCity: "",
      coordenadasCliente: router?.coordenadas || "",
      enabledFetchNaps: false,
    });

  ///* fetch data
  const {
    data: nodosPaging,
    isLoading: isLoadingNodos,
    isRefetching: isRefetchingNodos,
  } = useFetchAllNodes({
    params: {
      page_size: 1000,
    },
  });

  const {
    data: oltPaging,
    isLoading: isLoadingOlts,
    isRefetching: isRefetchingOlts,
  } = useFetchOLTs({
    enabled: !!watchedNodo,
    params: {
      page_size: 1000,
      nodo: watchedNodo,
    },
  });

  ///* mutations
  const createRouterMutation = useCreateRouter({
    navigate,
    returnUrl: returnUrlRoutesrPage,
  });
  const updateRouterMutation = useUpdateRouter({
    navigate,
    returnUrl: returnUrlRoutesrPage,
  });

  ///* handlers
  const onSave = async (data: SaveFormData) => {
    if (!isValid) {
      return;
    }

    ///* upd
    if (router?.id_router) {
      updateRouterMutation.mutate({ id: router.id_router, data });
      return;
    }

    ///* create
    createRouterMutation.mutate(data);
  };

  ///* effects
  useEffect(() => {
    if (!router?.id_router) return;
    reset(router);
  }, [router, reset]);

  // get current location
  useEffect(() => {
    if (router?.id_router) return;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setValue("coordenadas", `${latitude},${longitude}`);
          setLatLng({
            lat: latitude,
            lng: longitude,
          });
          setCurrentCoordenadas(`${latitude},${longitude}`);
        },
        (error) => {
          console.log(error);
        }
      );
    } else {
      setAlert(
        true,
        "La geolocalización no es compatible con este navegador.",
        AlertSeverityEnum.error
      );
    }
  }, [router]);

  // alerts: no olts
  useEffect(() => {
    if (isLoadingNodos || isLoadingOlts || !watchedNodo) return;

    if (!oltPaging?.data?.data.length)
      return setAlert(
        true,
        "No se encontraron OLTs para el nodo seleccionado",
        AlertSeverityEnum.error
      );
  }, [isLoadingNodos, isLoadingOlts, oltPaging]);

  useLoaders(
    isLoadingNodos || isRefetchingNodos || isLoadingOlts || isRefetchingOlts
  );

  if (
    !!router &&
    (isLoadingOlts || isRefetchingOlts || isLoadingNodos || isRefetchingNodos)
  )
    return null;

  return (
    <SingleFormBoxScene
      titlePage={title}
      onCancel={() => navigate(returnUrlRoutesrPage)}
      onSave={handleSubmit(onSave, () => {
        const auxErrors = Object.entries(errors).map(([key, { message }]) => {
          console.log(`Problems with  ${key}.`);
          return `${message}`;
        });
        auxErrors.forEach((errorToShow) => {
          setAlert(true, errorToShow, AlertSeverityEnum.error);
        });
        console.log(errors);
      })}
    >
      <CustomTextField
        label="Nombre"
        name="nombre"
        control={form.control}
        defaultValue={form.getValues().nombre}
        error={errors.nombre}
        helperText={errors.nombre?.message}
        type="none"
        // size={gridSizeMdLg6}
      />
      <CustomAutocompleteArrString
        label="Tipo de Router"
        name="tipo_router"
        control={form.control}
        defaultValue={form.getValues("tipo_router")}
        options={TIPOS_ROUTER_CHOICE}
        isLoadingData={false}
        error={errors.tipo_router}
        helperText={errors.tipo_router?.message}
        disableClearable
        // size={gridSizeMdLg6}
      />
      <CustomTextField
        label="Modelo"
        name="modelo"
        control={form.control}
        defaultValue={form.getValues("modelo")||""}
        error={errors.modelo}
        helperText={errors.modelo?.message}
        required={false}
        // size={gridSizeMdLg6}
      />

      <CustomAutocompleteOptimized<Nodo>
        label="Nodo"
        name="nodo"
        // options
        options={nodosPaging?.data.data || []}
        valueKey="nombre" // code is the fk
        actualValueKey="codigo"
        defaultValue={form.getValues().nodo || ""}
        optionLabelForEdit={form.getValues().nodo || ""}
        isLoadingData={isLoadingNodos}
        // validation
        control={form.control}
        error={errors.nodo}
        helperText={errors.nodo?.message}
        size={gridSizeMdLg6}
        disableClearable
        onChangeValue={() => {
          form.setValue("olt", "");
        }}
        onChangeRawValue={(rawValue)=>{
          form.setValue("coordenadas",rawValue.coordenadas)
          let s = rawValue.coordenadas.split(",");
          setLatLng({ lat: parseFloat(s[0]), lng: parseFloat(s[1]) });
          setCurrentCoordenadas(rawValue.coordenadas);
        }}
      />
      <CustomAutocomplete<OLT>
        label="OLT"
        name="olt"
        // options
        options={oltPaging?.data.data || []}
        valueKey="nombre" // code is the fk
        actualValueKey="codigo"
        defaultValue={form.getValues().olt || ""}
        optionLabelForEdit={form.getValues().olt || ""}
        isLoadingData={isLoadingOlts}
        // validation
        control={form.control}
        error={errors.olt}
        helperText={errors.olt?.message}
        size={gridSizeMdLg6}
        disableClearable
        disabled={!watchedNodo}
      />

      <CustomTextField
        label="IP"
        name="ip"
        control={form.control}
        defaultValue={form.getValues().ip}
        error={errors.ip}
        helperText={errors.ip?.message}
        size={gridSizeMdLg6}
      />

      <CustomDynamicSearch<Ipv4Group>
        label="Grupo IPV4"
        name='grupo_ipv_4'
        valueKey={"nombre"}
        actualValueKey={"codigo"}
        defaultValue={router?.grupo_ipv_4 || ""}
        functionToGetData={getIpv4Groups}
        paramsToGetData={{
          es_bras:false
        }}
        error={errors.grupo_ipv_4}
        control={form.control}
        helperText={errors.grupo_ipv_4?.message || "Escriba el nombre del grupo IPV4 a escoger"}
        size={gridSizeMdLg6}
        // disabled={!!!form.getValues()?.es_bras}
      />

      <CustomTextField
        label="Hostname/Username"
        name="username"
        control={form.control}
        defaultValue={form.getValues().username}
        error={errors.username}
        helperText={errors.username?.message}
        size={gridSizeMdLg6}
        type="none"
        // overrideAsPassword
      />

      <CustomPasswordTextField
        label="Password"
        name="password"
        errors={errors}
        form={form}
        size={gridSizeMdLg6}
        required={false}
      />

      {/* <CustomTextField
        label="Usuario API"
        name="usuario_api"
        control={form.control}
        defaultValue={form.getValues().usuario_api}
        error={errors.usuario_api}
        helperText={errors.usuario_api?.message}
        size={gridSizeMdLg6}
        type="none"
      /> */}
      <CustomNumberTextField
        label="Puerto API"
        name="puerto_api"
        control={form.control}
        defaultValue={form.getValues().puerto_api}
        error={errors.puerto_api}
        helperText={errors.puerto_api?.message}
        size={gridSizeMdLg6}
        shrink={!!form.getValues().puerto_api}
      />
      {/* <CustomPasswordTextField
        label="Clave API"
        name="clave_api"
        errors={errors}
        form={form}
        size={gridSizeMdLg6}
      /> */}

      <SwitchComponenteForm
        label={form.watch()?.estado ? 'Activo' : 'Inactivo'}
        size={gridSizeMdLg6}
        control={form.control}
        defaultValue={!!form.getValues()?.estado}
        name="estado"
      />

      <CustomTextField
        label="Dirección"
        name="direccion"
        control={form.control}
        defaultValue={form.getValues().direccion}
        error={errors.direccion}
        helperText={errors.direccion?.message}
        required={false}
      />
      {/* <CustomTextField
        label="Coordenadas"
        name="coordenadas"
        control={form.control}
        defaultValue={form.getValues().coordenadas}
        error={errors.coordenadas}
        helperText={errors.coordenadas?.message}
      /> */}

      <CustomTextField
        type="coordenada"
        control={form.control}
        name="coordenadas"
        defaultValue={form.getValues().coordenadas}
        size={{
          xs: 11,
          sm: 11,
          md: 11,
          lg: 11,
        }}
        label={texto.coordenadas}
        error={errors.coordenadas}
        startAdornment={
          <InputAdornment position="start">
            <SvgIcon fontSize="small">
              <GlobeEuropeAfricaIcon />
            </SvgIcon>
          </InputAdornment>
        }
        helperText={errors.coordenadas?.message}
        onChangeValue={(e, v) => {
          if (v) {
            let s = e.split(",");
            setLatLng({ lat: s[0], lng: s[1] });
            setCurrentCoordenadas(e);
          }
        }}
      />

      {currentCoordenadas && (
        <Map coordenadas={latLng} napsByCity={[]} setLatLng={setLatLng} />
      )}
    </SingleFormBoxScene>
  );
};

export default SaveRouter;
